/**
 * Test mode: the toggle in the app's header.
 *
 * On: every screen shows the golden records (src/data/golden) from a private
 * sandbox on the server, and unfinished features are shown too. Off: only real
 * data, and only features that actually work.
 *
 * Remembered per browser. Sent to the server as `x-test-mode: on` on every
 * request (src/adapters/rpc/client.ts), which is what swaps the data — the
 * browser never holds a second copy of the rules.
 */
import { useSyncExternalStore } from 'react'

const KEY = 'test-mode'
const listeners = new Set<() => void>()

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on'
  } catch {
    // Private windows and blocked storage: fall back to real data.
    return false
  }
}

let current = typeof window === 'undefined' ? false : read()

export function isTestMode(): boolean {
  return current
}

export function setTestMode(on: boolean): void {
  current = on
  try {
    localStorage.setItem(KEY, on ? 'on' : 'off')
  } catch {
    // Still takes effect for this tab, just not remembered.
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useTestMode(): boolean {
  return useSyncExternalStore(subscribe, isTestMode, () => false)
}
